import type { OrderStep } from '@/components/order/OrderTypes'
import { type UiConfig, type UiOrderStatusStepConfig } from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import { copyWithoutSigning } from '@/utils/copyWithoutSigning'
import { ChartPie, ClipboardList, Dna, Microscope, OrderedList } from 'flowbite-react-icons/outline'
import { useMemo } from 'react'

/** All flowbite outline icons used here share this component signature. */
type StepIcon = typeof Dna

export interface ConfiguredOrderStep extends OrderStep {
  icon: StepIcon
}

const DEFAULT_STEP_ICONS: Record<string, StepIcon> = {
  order: Dna,
  collection: ClipboardList,
  analysis: Microscope,
  guidance: OrderedList,
  results: ChartPie,
}

/** Standard lab pipeline when config does not supply ``orderStatusSteps``. */
const WORKFLOW_FALLBACK: ReadonlyArray<UiOrderStatusStepConfig> = [
  { id: 'order', label: 'Complete & Sign Order' },
  { id: 'collection', label: 'Sample Collection' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'results', label: 'Lab Report' },
]

/**
 * Resolve the raw step list: prefer API config; otherwise use the standard
 * lab pipeline so in-progress / results pages can map backend status to known
 * step ids even when the microapp omits ``orderStatusSteps``.
 */
export function resolveOrderStatusSource(
  orderStatusSteps: UiOrderStatusStepConfig[] | undefined
): UiOrderStatusStepConfig[] {
  if (orderStatusSteps?.length) {
    return orderStatusSteps
  }
  return [...WORKFLOW_FALLBACK]
}

export interface UseConfiguredOrderStepsOptions {
  /** Step id whose status is ``current``; earlier ``completed``, later ``pending``. */
  currentStepId?: string
  /** Zero-based index into the *post-filter* step list for ``current``. */
  currentStepIndex?: number
}

/**
 * Build timeline steps from UiConfig (``orderStatusSteps`` + ``featureFlags.guidance``).
 *
 * The ``guidance`` step is authoritatively gated by
 * ``featureFlags.guidance``: it renders only when the flag is explicitly
 * ``true``. When the flag is ``false`` or missing, any ``guidance`` entry in
 * ``orderStatusSteps`` is filtered out so tenants that haven't opted into the
 * Guidance feature don't see a dangling step in the timeline.
 *
 * When DocuSign TRF e-signature is off, "Sign" is stripped from step labels
 * (fallback "Complete & Sign Order" and config "Create & Sign Order") so
 * opted-out orgs are not told a signature will happen.
 *
 * Exported for unit tests; pages should prefer {@link useConfiguredOrderSteps}.
 */
export function buildConfiguredOrderSteps(
  config: Pick<UiConfig, 'orderStatusSteps' | 'featureFlags' | 'docusign'>,
  options: UseConfiguredOrderStepsOptions = {}
): ConfiguredOrderStep[] {
  const { currentStepId, currentStepIndex } = options
  const source = resolveOrderStatusSource(config.orderStatusSteps)
  const filtered = config.featureFlags?.guidance === true ? source : source.filter((step) => step.id !== 'guidance')
  // Same boolean-true gate as isTrfSignatureDocusignEnabled (stringly "true" stays off).
  const docusignEnabled = config.docusign?.enabled === true

  let resolvedIndex = 0
  if (currentStepId) {
    const found = filtered.findIndex((step) => step.id === currentStepId)
    resolvedIndex = found >= 0 ? found : 0
  } else if (typeof currentStepIndex === 'number' && !Number.isNaN(currentStepIndex)) {
    resolvedIndex = Math.max(0, Math.min(currentStepIndex, Math.max(0, filtered.length - 1)))
  }

  return filtered.map((step, index) => ({
    id: step.id,
    label: copyWithoutSigning(step.label, docusignEnabled),
    status: index < resolvedIndex ? 'completed' : index === resolvedIndex ? 'current' : 'pending',
    icon: DEFAULT_STEP_ICONS[step.id] ?? ChartPie,
  }))
}

export function useConfiguredOrderSteps(options: UseConfiguredOrderStepsOptions = {}): ConfiguredOrderStep[] {
  const { config } = useUiConfig()
  const { currentStepId, currentStepIndex } = options
  return useMemo(
    () => buildConfiguredOrderSteps(config, { currentStepId, currentStepIndex }),
    // `buildConfiguredOrderSteps` only reads these specific `config` sub-fields.
    // Depending on the whole `config` object would recompute on every unrelated
    // config change (new object identity each load) with no behavior difference,
    // so the deps are intentionally narrowed to the fields actually consumed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.orderStatusSteps, config.featureFlags?.guidance, config.docusign?.enabled, currentStepId, currentStepIndex]
  )
}
